// ============================================================
// VCS.Captions — Deepgram-Streaming, PCM-Pipeline, Caption-UI
// ============================================================
window.VCS = window.VCS || {};

VCS.Captions = class {

    // ── Konstanten ───────────────────────────────────────────

    // Anzeigedauer einer Caption-Zeile. Fuer alle Engines ausser Azure ist das
    // weiterhin der einzige Wert: feste 8 s, bei jeder Aktualisierung neu.
    // Im Azure-Pfad ist es die Untergrenze der Lesezeit eines FERTIGEN Satzes
    // -- siehe _hideDelayMs().
    static CAPTION_HIDE_MS = 8000;
    // Ab hier nur Azure.
    // Obergrenze -- sonst blockiert ein Satz am 280-Zeichen-Limit die Bahn.
    static CAPTION_HIDE_MAX_MS = 20000;
    // Lesetempo. 70 ms/Zeichen sind rund 14 Zeichen je Sekunde: langsamer als
    // die ueblichen 15-20 fuer Untertitel in der Muttersprache, weil hier eine
    // Uebersetzung gelesen wird, die man nicht schon halb erwartet hat.
    static CAPTION_MS_PER_CHAR = 70;
    // Not-Netz fuer einen laufenden Satz, dessen Final nie kommt (Abbruch,
    // Engine-Wechsel mitten im Satz). Im Normalfall raeumt das Final auf.
    static CAPTION_STALE_MS = 30000;
    static CAPTION_MAX_LINES = 4;
    static SELF_TRANSCRIPT_MAX_LINES = 4;
    static CAPTION_PCM_SAMPLE_RATE = 16000;

    // Sprechtempo-Hinweis.
    //
    // Azure schliesst einen Satz erst bei einer Sprechpause ab. Wer ohne Pausen
    // durchspricht, haelt die Erkennung offen: die Uebersetzung wird laufend
    // umgebaut, der fertige Satz kommt als Block am Stueck, und ab 280 Zeichen
    // schneidet der Server ihn ab (caption.js sendTranslated). Fuer den
    // Empfaenger ist das kaum lesbar -- deshalb bekommt der SPRECHER ein
    // Zeichen, solange sein laufender Satz zu lang wird.
    //
    // 10 s: ein normaler Satz dauert 3-6 s, ein langer Gedanke 8-10 s. Darueber
    // ist es eindeutig zu lang, und bis zur 280-Zeichen-Grenze (~15 s) bleibt
    // noch Luft, damit die Pause ueberhaupt noch etwas rettet.
    static SPEECH_PACE_THRESHOLD_MS = 10000;
    // Kommt so lange gar kein Ereignis mehr, gilt der Satz als vorbei. Nur ein
    // Netz gegen ein haengendes Symbol -- im Normalfall raeumt das Final auf.
    static SPEECH_PACE_IDLE_MS = 4000;
    // Standzeit der Erklaerung. Bewusst UNABHAENGIG vom Symbol: das kann nach
    // zwei Sekunden wieder aus sein, dann waere die einmalige Erklaerung weg,
    // bevor sie jemand gelesen hat.
    static SPEECH_PACE_EXPLAIN_MS = 8000;
    // Merker, dass die Erklaerung schon lief -- pro Geraet, nie in der DB.
    static SPEECH_PACE_SEEN_KEY = 'chat-pace-hint-seen';

    // Limiter fuer den Caption-Zweig. Android-AGC faehrt den Pegel bis an die
    // Vollaussteuerung; _convertFloat32ToInt16Mono klemmt dann hart auf +-1 und
    // verzerrt genau die lauten Stellen, an denen die Erkennung am meisten zu
    // tun hat. Der Limiter nimmt die Spitzen vorher weich zurueck.
    //
    // Look-Ahead ist der komplette ScriptProcessor-Block (2048 Samples @16 kHz
    // = 128 ms): der Block-Peak steht fest, bevor ein Sample gesendet wird.
    // Absenken gilt sofort fuer den ganzen Block, das Zurueckfahren laeuft
    // ueber LIMITER_RELEASE_MS -- sonst pumpt der Pegel zwischen den Bloecken.
    static LIMITER_CEILING       = 0.95;   // ~-0.45 dBFS
    static LIMITER_RELEASE_MS    = 400;    // Gain-Verdopplung pro 400 ms
    // Ab hier gilt ein Sample als "am Anschlag". Knapp unter 1.0, weil das
    // AGC-Signal die Grenze nie exakt trifft.
    static CLIP_SAMPLE_THRESHOLD = 0.995;

    // ── Konstruktor ──────────────────────────────────────────

    constructor() {
        this._captionStarting = false;
        // rAF-Coalesce fuer Streaming-Partials (1:1, globale Bahn, siehe showTranslated)
        this._partialRafId = 0;
        this._partialPending = null;

        // Group-Mode: pro Sprecher-ID ein eigenes rAF/Pending-Slot.
        // Schluessel = senderId (msg.from). Wert = { rafId, pending: {text, dimItalic, phaseTag} | null,
        //                                            hideTimer }.
        // Siehe _renderToGroupTile()/_flushGroupPartial(). State wird erst in releasePipeline()
        // freigegeben bzw. wenn ein Tile aus dem Grid verschwindet.
        this._groupSpeakerState = new Map();

        // Sprechtempo-Hinweis: Uhr laeuft ab dem ersten Zwischenstand eines
        // Satzes und wird vom Final wieder abgeraeumt (siehe _paceOnSelfText).
        this._paceShowTimer = 0;
        this._paceIdleTimer = 0;
        this._paceExplainTimer = 0;
        this._paceVisible = false;

        // View-Cache
        this._view = {
            btnCaptionRecord     : $('#btnCaptionRecord'),
            translatedCaption    : $('#translatedCaption'),
            translatedCaptionList: $('#translatedCaptionList'),
            selfTranscript       : $('#selfTranscript'),
            selfTranscriptList   : $('#selfTranscriptList'),
            speechPaceHint       : $('#speechPaceHint'),
            speechPaceHintText   : $('#speechPaceHintText'),
        };

        // Antippen erklaert das Symbol. Bewusst nur auf Nachfrage -- so muss
        // nichts von selbst aufpoppen, und die Erklaerung steht dort, wo die
        // Frage entsteht (der Messenger hat keine eigene Hilfe-Seite, die FAQ
        // liegt im Webfrontend).
        this._view.speechPaceHint.on('click', () => this._paceShowExplain());
    }

    // ── Öffentliche Methoden ─────────────────────────────────

    // Konfiguration an Server schicken
    sendConfig() {
        const normalized = VCS.i18n.normalizeLang(VCS.state.currentLang);
        const enabled = !!VCS.state.captionsEnabled || !!VCS.state.captionOnlyMode;
        this._wsSend({
                         type       : 'caption-config',
                         id         : VCS.state.myId,
                         enabled    : enabled,
                         lang       : normalized,
                         captionOnly: !!VCS.state.captionOnlyMode,
                     });
    }

    // Caption-Pipeline starten (lock-gesichert)
    async startAutoCapture() {
        if (this._captionStarting ||
            (!VCS.state.captionsEnabled && !VCS.state.captionOnlyMode && !VCS.state.summaryCapture) ||
            VCS.state.captionPaused ||
            VCS.state.captionStreaming) {
            return;
        }
        // Der Server hat die Live-Uebersetzung gesperrt (kein Kontingent, kein
        // Guthaben, keine Freigabe mehr). Ohne diese Abfrage liefe die Aufnahme
        // trotzdem los: die Sperrmeldung kommt schon beim Call-Aufbau, da steht
        // captionStreaming noch auf false und onExpired findet nichts zum
        // Anhalten. Ergebnis waere ein Geraet, das minutenlang PCM hochschickt,
        // das der Server wegwirft -- neben einem Hinweis "Kontingent
        // aufgebraucht" und einem aktiv aussehenden Mikrofon.
        if (VCS.liveCapNotice?.isBlocked?.()) return;
        this._captionStarting = true;

        const myNorm = VCS.i18n.normalizeLang(VCS.state.currentLang);
        const peerLang = VCS.state.peerLang;

        if (!VCS.state.groupCallActive && !VCS.state.captionOnlyMode && !VCS.state.summaryCapture
            && (!peerLang || peerLang === myNorm)) {
            this._captionStarting = false;
            return;
        }
        if (!VCS.state.ws || VCS.state.ws.readyState !== WebSocket.OPEN) {
            this._captionStarting = false;
            return;
        }


        // Audio-Stream: localStream wiederverwenden wenn vorhanden (vermeidet
        // Konflikte bei doppeltem getUserMedia, besonders in Group Calls),
        // sonst separaten Stream holen.
        const existingStream = VCS.state.localStream;
        const hasAudio = existingStream?.getAudioTracks().some(t => t.readyState === 'live');
        if (hasAudio) {
            // Nur-Audio-Klon des bestehenden Streams
            VCS.state.captionPcmStream = new MediaStream(existingStream.getAudioTracks());
            VCS.state._captionUsesLocalStream = true;
        } else {
            try {
                VCS.state.captionPcmStream = await navigator.mediaDevices.getUserMedia({
                                                                                           audio: {
                                                                                               channelCount    : 1,
                                                                                               echoCancellation: true,
                                                                                               noiseSuppression: true,
                                                                                               autoGainControl : true,
                                                                                           },
                                                                                           video: false,
                                                                                       });
                VCS.state._captionUsesLocalStream = false;
            } catch (err) {
                console.warn('[caption-pcm] Mikrofon-Zugriff fehlgeschlagen:', err);
                this._captionStarting = false;
                return;
            }
        }

        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) {
            console.warn('[caption-pcm] Kein AudioContext verfügbar');
            VCS.state.captionPcmStream.getTracks().forEach(t => t.stop());
            VCS.state.captionPcmStream = null;
            this._captionStarting = false;
            return;
        }

        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        // Aufnahme-Kontext direkt in der Zielrate anlegen (16 kHz). Dann
        // rechnet der Browser den Mikrofon-Stream selbst herunter -- mit
        // einem richtigen Antialiasing-Filter.
        //
        // Vorher lief der Kontext in Mikrofon-Rate (48 kHz) und
        // _convertFloat32ToInt16Mono mittelte je 3 Samples zusammen. Das ist
        // ein viel zu flacher Filter: bei 12 kHz daempft er nur ~10 dB, und
        // diese Anteile falten sich auf 4 kHz zurueck -- genau dort liegen
        // s/sch/f/t. Die Vokale kamen sauber an, die Konsonanten verschmiert,
        // und die entscheiden ueber das erkannte Wort.
        //
        // Faellt der Browser auf eine andere Rate zurueck, greift der alte
        // Downsample-Pfad automatisch (er liest inputBuffer.sampleRate).
        try {
            VCS.state.captionAudioCtxPcm = new AudioCtx({
                sampleRate: VCS.Captions.CAPTION_PCM_SAMPLE_RATE,
            });
        } catch (_) {
            const trackSettings = VCS.state.captionPcmStream.getAudioTracks()[0]?.getSettings?.() || {};
            const streamSampleRate = Number(trackSettings.sampleRate);
            // Safari: sampleRate oft nicht in trackSettings → explizit 48000 setzen
            const ctxRate = Number.isFinite(streamSampleRate) && streamSampleRate > 0
                ? streamSampleRate
                : (isSafari ? 48000 : undefined);
            VCS.state.captionAudioCtxPcm = ctxRate
                ? new AudioCtx({sampleRate: ctxRate})
                : new AudioCtx();
        }
        console.warn('[caption-pcm] Aufnahme-Rate=' + VCS.state.captionAudioCtxPcm.sampleRate
            + 'Hz (Ziel ' + VCS.Captions.CAPTION_PCM_SAMPLE_RATE + 'Hz)');

        if (VCS.state.captionAudioCtxPcm.state === 'suspended') {
            await VCS.state.captionAudioCtxPcm.resume().catch(() => {
            });
        }
        // Android 17 / One UI 9 (04.10.): Sobald der Gespraechston startet,
        // stellt das System die Audio-Ausgabe auf Telefonie um. Dabei wird
        // dieser 16-kHz-Kontext angehalten ('suspended') und nie wieder
        // gestartet -- onaudioprocess feuert nicht mehr, es geht kein PCM raus.
        // Kontexte in der Geraeterate (48 kHz, z.B. audio-monitor) laufen
        // weiter. Ein Wiederanwerfen in onaudioprocess greift deshalb nicht;
        // es muss hier am statechange passieren, fuer alle Browser. Begrenzt
        // auf wenige Versuche pro Aufnahme, damit kein Endlos-Pingpong entsteht.
        const AUTO_RESUME_MAX = 5;
        const pcmCtx = VCS.state.captionAudioCtxPcm;
        let autoResumeCount = 0;
        pcmCtx.onstatechange = () => {
            // Nur solange dieser Kontext noch der aktive ist -- releasePipeline()
            // setzt captionAudioCtxPcm auf null und schliesst ihn.
            if (pcmCtx.state !== 'suspended' || VCS.state.captionAudioCtxPcm !== pcmCtx) {
                return;
            }
            console.warn('[caption-pcm] ctx statechange -> suspended');
            if (autoResumeCount >= AUTO_RESUME_MAX) {
                console.warn('[caption-pcm] auto-resume aufgegeben nach ' + AUTO_RESUME_MAX + ' Versuchen');
                return;
            }
            autoResumeCount++;
            const attempt = autoResumeCount;
            pcmCtx.resume().then(() => {
                console.warn('[caption-pcm] auto-resume ok #' + attempt + ' state=' + pcmCtx.state);
            }).catch((err) => {
                console.warn('[caption-pcm] auto-resume fehlgeschlagen #' + attempt + ': ' + (err?.message || err));
            });
        };

        VCS.state.captionSourceNode = VCS.state.captionAudioCtxPcm.createMediaStreamSource(
            VCS.state.captionPcmStream
        );

        // Gain-Node: Signal-Pegel unveraendert durchreichen (1.0 = unity).
        // Frueher 3.0 -> in Verbindung mit autoGainControl=true im Mic-Constraint
        // (Pegel ~-3 dBFS) fuehrte der Boost zu hartem Clipping und damit zu
        // schlechter Deepgram-Transkription. Boost weg.
        //
        // Achtung beim Nachlesen: die Mic-Constraints oben (echoCancellation /
        // noiseSuppression / autoGainControl) greifen nur, wenn hier KEIN
        // localStream vorlag. Im normalen Video-Call ist hasAudio true, dann
        // wird der Call-Track geklont und es gelten die Constraints aus
        // VCS.Media.getAudioConstraints(). Der Pegel ist auf diesem Pfad also
        // nicht ueber die Constraints steuerbar, ohne den Gespraechston
        // mitzuveraendern -- deshalb sitzt die Pegelbremse unten im
        // onaudioprocess und nicht am getUserMedia-Aufruf.
        const captionGain = VCS.state.captionAudioCtxPcm.createGain();
        captionGain.gain.value = 1.0;
        VCS.state.captionSourceNode.connect(captionGain);

        // ScriptProcessorNode, 1 In/Out-Kanal.
        //
        // Puffergroesse an die Kontext-Rate koppeln, damit die Frame-DAUER
        // ungefaehr gleich bleibt -- der Node nimmt nur Zweierpotenzen:
        //   16 kHz * 2048 = 128 ms      48 kHz * 4096 = 85 ms
        // Fest verdrahtete 4096 ergaeben bei 16 kHz 256 ms: dreimal traeger
        // als vorher, spuerbar in der Uebersetzungslatenz, und fuer das
        // serverseitige VAD zu grob (Pre-Roll ist nur 250 ms lang, da passt
        // dann kein ganzer Frame mehr rein).
        const _ctxRate = VCS.state.captionAudioCtxPcm.sampleRate;
        const bufferSize = _ctxRate <= 24000 ? 2048 : 4096;
        VCS.state.captionWorkletNode = VCS.state.captionAudioCtxPcm.createScriptProcessor(
            bufferSize, 1, 1
        );

        // Pegel-Diagnose: alle ~5s ein Log mit Peak/RMS in dBFS und
        // CLIP-Marker, damit wir sehen ob das Mic zu leise / am Anschlag ist.
        // Re-init pro Capture-Session, kein Cross-Call-Leak.
        //
        // Peak allein reicht zur Beurteilung nicht: er ist das Maximum ueber
        // ein 5-s-Fenster, ein einziges Sample an der Grenze setzt schon den
        // CLIP-Marker. Deshalb zusaetzlich clip= (Anteil Samples am Anschlag)
        // und run= (laengste ununterbrochene Kette solcher Samples). Einzelne
        // Spitzen -> run bleibt bei 1-2, harmlos. Flache Plateaus -> run geht
        // zweistellig, dann ist das Signal schon VOR uns geclippt (AGC im
        // Browser) und der Limiter unten kann nichts mehr retten; dann muessen
        // die Constraints in VCS.Media.getAudioConstraints() ran.
        const _captionIsSafari = isSafari;
        const PEGEL_LOG_INTERVAL_MS = 5000;
        let pegelPeak = 0;
        let pegelSumSq = 0;
        let pegelSamples = 0;
        let pegelLastLogAt = 0;
        let pegelClipped = 0;      // Samples >= CLIP_SAMPLE_THRESHOLD im Fenster
        let pegelClipRun = 0;      // laengster Anschlag-Lauf im Fenster
        let pegelGainMin = 1;      // staerkste Limiter-Absenkung im Fenster

        // Limiter-State. blockMs aus der echten Blockdauer, damit das Release
        // bei 48 kHz (4096 Samples = 85 ms) genauso lange braucht wie bei
        // 16 kHz (2048 Samples = 128 ms).
        const LIM_CEILING  = VCS.Captions.LIMITER_CEILING;
        const LIM_THRESH   = VCS.Captions.CLIP_SAMPLE_THRESHOLD;
        const _blockMs     = (bufferSize / _ctxRate) * 1000;
        const LIM_RELEASE  = Math.pow(2, _blockMs / VCS.Captions.LIMITER_RELEASE_MS);
        let limGain        = 1;
        let limBuf         = null;   // Arbeitspuffer, lazy + wiederverwendet
        let limRun         = 0;      // laufender Anschlag-Lauf, blockuebergreifend

        VCS.state.captionWorkletNode.onaudioprocess = (event) => {
            if (!VCS.state.captionStreaming || VCS.state.captionPaused || VCS.state.audioMuted) {
                return;
            }
            if (!VCS.state.ws || VCS.state.ws.readyState !== WebSocket.OPEN) {
                return;
            }

            // Safari: AudioContext kann im Hintergrund suspendiert werden
            if (_captionIsSafari && VCS.state.captionAudioCtxPcm?.state === 'suspended') {
                VCS.state.captionAudioCtxPcm.resume().catch(() => {});
                return;
            }
            const float32 = event.inputBuffer.getChannelData(0);
            const blockLen = float32.length;

            // Pegel-Aggregation (vor dem Safari-Empty-Skip damit auch Stille im
            // Log sichtbar wird). Peak fuer Clipping-Detection, RMS fuer Energie,
            // clip/run zur Unterscheidung Einzelspitze vs. Plateau. Gemessen
            // wird das ROHsignal, vor dem Limiter -- sonst verdeckt der Limiter
            // genau das, was wir wissen wollen.
            let blockPeak = 0;
            for (let i = 0; i < blockLen; i++) {
                const v = float32[i];
                const a = v < 0 ? -v : v;
                if (a > blockPeak) blockPeak = a;
                pegelSumSq += v * v;
                if (a >= LIM_THRESH) {
                    pegelClipped++;
                    limRun++;
                    if (limRun > pegelClipRun) pegelClipRun = limRun;
                } else {
                    limRun = 0;
                }
            }
            if (blockPeak > pegelPeak) pegelPeak = blockPeak;
            pegelSamples += blockLen;

            // Look-Ahead-Limiter: der Block-Peak steht fest, bevor ein Sample
            // rausgeht. Absenken gilt sofort fuer den ganzen Block (die Spitze
            // kann gleich am Blockanfang liegen), Zurueckfahren nur schrittweise
            // -- sonst pumpt der Pegel zwischen den Bloecken hoerbar.
            const limWanted = blockPeak > LIM_CEILING ? (LIM_CEILING / blockPeak) : 1;
            if (limWanted < limGain) {
                limGain = limWanted;
            } else if (limGain < 1) {
                limGain = Math.min(limWanted, limGain * LIM_RELEASE, 1);
            }
            if (limGain < pegelGainMin) pegelGainMin = limGain;

            let pcmSource = float32;
            if (limGain < 1) {
                if (!limBuf || limBuf.length !== blockLen) {
                    limBuf = new Float32Array(blockLen);
                }
                for (let i = 0; i < blockLen; i++) {
                    limBuf[i] = float32[i] * limGain;
                }
                pcmSource = limBuf;
            }

            const _pegelNow = Date.now();
            if (pegelLastLogAt === 0) pegelLastLogAt = _pegelNow;
            if (_pegelNow - pegelLastLogAt >= PEGEL_LOG_INTERVAL_MS && pegelSamples > 0) {
                const rms = Math.sqrt(pegelSumSq / pegelSamples);
                const peakDb = pegelPeak > 0 ? (20 * Math.log10(pegelPeak)).toFixed(1) : '-inf';
                const rmsDb  = rms > 0       ? (20 * Math.log10(rms)).toFixed(1)       : '-inf';
                const clip = pegelPeak >= 0.99 ? ' CLIP' : '';
                const clipPct = (pegelClipped * 100 / pegelSamples).toFixed(2);
                // console.warn statt .log: console.log/.info sind im Messenger
                // stumm geschaltet (core/error-logger.js), nur warn/error gehen
                // ueber den client-log-Channel ins Backend-Log. Als .log war
                // diese Pegel-Diagnose faktisch tot.
                console.warn('[caption-pegel] peak=' + peakDb + 'dBFS rms=' + rmsDb
                    + 'dBFS samples=' + pegelSamples
                    + ' clip=' + pegelClipped + '(' + clipPct + '%)'
                    + ' run=' + pegelClipRun
                    + ' gmin=' + pegelGainMin.toFixed(2) + clip);
                pegelPeak = 0;
                pegelSumSq = 0;
                pegelSamples = 0;
                pegelClipped = 0;
                pegelClipRun = 0;
                pegelGainMin = 1;
                pegelLastLogAt = _pegelNow;
            }

            // Hinweis: Frueher hier ein Safari-Empty-Skip, der Stille-Frames
            // verwarf. Entfernt 2026-05-09 -- OpenAI Realtime braucht die Stille-
            // Frames fuer VAD/Endpointing. Ohne sie fielen iPhone-Utterances
            // mehrheitlich auf den 1500 ms Idle-Timer (sentence-final klappt
            // nicht), Wartezeiten bis zu 5 s. Mit Stille-Frames erkennt die
            // Engine Sprech-Pausen sauber und triggert sentence-final.

            const inputRate = event.inputBuffer.sampleRate || VCS.state.captionAudioCtxPcm.sampleRate;
            const int16 = this._convertFloat32ToInt16Mono(
                pcmSource, inputRate, VCS.Captions.CAPTION_PCM_SAMPLE_RATE
            );
            if (int16.length === 0) {
                return;
            }
            VCS.signaling?.sendBinary(int16.buffer);
        };

        captionGain.connect(VCS.state.captionWorkletNode);
        // Stummer Gain → destination: ScriptProcessor braucht einen verbundenen
        // Output damit onaudioprocess feuert, aber wir wollen das Mikrofon-Signal
        // NICHT über die Lautsprecher ausgeben (sonst Echo).
        const muteGain = VCS.state.captionAudioCtxPcm.createGain();
        muteGain.gain.value = 0;
        VCS.state.captionWorkletNode.connect(muteGain);
        muteGain.connect(VCS.state.captionAudioCtxPcm.destination);

        // Server informieren: Deepgram-Stream öffnen
        this._wsSend({
                         type: 'caption-start',
                         id  : VCS.state.myId
                     });

        VCS.state.captionStreaming = true;
        VCS.state.captionEngineActive = true;
        VCS.state.captionRecording = true;
        this._captionStarting = false;
        this.updateFabUI();
        // Audio-Fader visibility re-check: pipeline just went active.
        // Engine-info itself is pushed asynchronously by the backend;
        // this call wires up the dependency so the fader appears as
        // soon as captionEngineActive flips to true (engine info may
        // already have arrived earlier).
        VCS.audioFader?.updateVisibility();
    }

    // Audio-Pipeline stoppen, State zurücksetzen
    releasePipeline() {
        VCS.state.captionStreaming = false;
        VCS.state.captionEngineActive = false;
        VCS.state.captionPaused = false;
        VCS.state.captionRecording = false;
        VCS.state.captionOnlyMode = false;

        // Sprechtempo-Uhr anhalten. Steht hier und nicht nur in
        // clearSelfTranscript(), weil e2e.js die Pipeline auch ohne das
        // Leeren des Transkripts freigibt -- das Symbol bliebe sonst stehen.
        this._paceReset();

        // Pending Partial-rAF abbrechen — sonst flusht ein veralteter Stand
        // nach dem Stop ins UI.
        if (this._partialRafId) {
            cancelAnimationFrame(this._partialRafId);
            this._partialRafId = 0;
        }
        this._partialPending = null;

        // Group-Mode: alle Sprecher-Slots leeren (Tiles existieren ggf. noch).
        this._clearGroupSpeakerState();
        const grid = document.getElementById('groupVideoGrid');
        if (grid) {
            grid.querySelectorAll('.group-video-caption').forEach(slot => {
                slot.textContent = '';
                slot.classList.add('hidden');
                slot.classList.remove('caption-interim');
            });
        }

        // Server informieren: Deepgram-Stream schließen
        if (VCS.state.ws && VCS.state.ws.readyState === WebSocket.OPEN) {
            this._wsSend({
                             type: 'caption-stop',
                             id  : VCS.state.myId
                         });
        }

        // Audio-Fader-Graph schliessen (openai-realtime engine only).
        // Wenn nie initialisiert, ist release() ein no-op.
        VCS.audioFader?.release();

        // Audio-Pipeline trennen
        if (VCS.state.captionWorkletNode) {
            VCS.state.captionWorkletNode.onaudioprocess = null;
            try {
                VCS.state.captionWorkletNode.disconnect();
            } catch (_) {
            }
            VCS.state.captionWorkletNode = null;
        }
        if (VCS.state.captionSourceNode) {
            try {
                VCS.state.captionSourceNode.disconnect();
            } catch (_) {
            }
            VCS.state.captionSourceNode = null;
        }
        if (VCS.state.captionAudioCtxPcm) {
            VCS.state.captionAudioCtxPcm.close().catch(() => {
            });
            VCS.state.captionAudioCtxPcm = null;
        }

        // Mikrofon-Stream stoppen (nicht wenn vom localStream geklont)
        if (VCS.state.captionPcmStream) {
            if (!VCS.state._captionUsesLocalStream) {
                VCS.state.captionPcmStream.getTracks().forEach(t => t.stop());
            }
            VCS.state.captionPcmStream = null;
            VCS.state._captionUsesLocalStream = false;
        }

        // CC-Toggle visuell zurücksetzen
        VCS.callControls?._view?.btnCaptionOnly?.toggleClass('btn-active-green', false);

        this.updateFabUI();
    }

    // ── Mitschrift-Erkennung (03.10.) ────────────────────────
    // 1:1, gleiche Sprache, Zusammenfassung laeuft: der Server meldet per
    // 'summary-capture', ob aufgenommen werden soll. CC steuert davon
    // unabhaengig nur die Anzeige.

    setSummaryCapture(on) {
        VCS.state.summaryCapture = !!on;
        console.log('[summary-capture] ' + (on ? 'an' : 'aus'));
        if (on) {
            this.resumeSummaryCapture();
            return;
        }
        // CC laeuft noch: die Pipeline gehoert weiter den Untertiteln.
        if (VCS.state.captionOnlyMode) {
            return;
        }
        if (VCS.state.captionStreaming) {
            this.releasePipeline();
        }
    }

    // Aufnahme starten, sobald der Call steht. Kommt summary-capture vor dem
    // Call-Start, holen 'approved' / 'start-call' (signaling.js) das nach.
    resumeSummaryCapture() {
        if (!VCS.state.summaryCapture || VCS.state.sessionState?.phase !== 'call') {
            return;
        }
        this.startAutoCapture().catch(() => {
        });
    }

    // CC aus bei laufender Mitschrift-Erkennung: nur die Anzeige endet, die
    // Aufnahme laeuft weiter. Der Server schickt dann keine Untertitel mehr.
    stopDisplayKeepCapture() {
        VCS.state.captionOnlyMode = false;
        this._paceReset();
        if (this._partialRafId) {
            cancelAnimationFrame(this._partialRafId);
            this._partialRafId = 0;
        }
        this._partialPending = null;
        VCS.callControls?._view?.btnCaptionOnly?.toggleClass('btn-active-green', false);
        this.updateFabUI();
    }

    // Caption-Pause umschalten
    async togglePause() {
        const inCallOrGroup = VCS.state.sessionState.phase === 'call' || VCS.state.groupCallActive;
        if ((!VCS.state.captionsEnabled && !VCS.state.captionOnlyMode) || !inCallOrGroup) {
            return;
        }
        const myNorm = VCS.i18n.normalizeLang(VCS.state.currentLang);
        const peerLang = VCS.state.peerLang;
        if (!VCS.state.groupCallActive && !VCS.state.captionOnlyMode && (!peerLang || peerLang === myNorm)) {
            return;
        }

        VCS.state.captionPaused = !VCS.state.captionPaused;

        if (VCS.state.captionPaused) {
            if (VCS.state.ws && VCS.state.ws.readyState === WebSocket.OPEN) {
                this._wsSend({
                                 type: 'caption-stop',
                                 id  : VCS.state.myId
                             });
            }
            this.clearTranslated();
            this.clearSelfTranscript();
            VCS.ui?.showToast?.(t('autoTranslateOff'), 'info', 1800);
        } else {
            if (VCS.state.ws && VCS.state.ws.readyState === WebSocket.OPEN) {
                this._wsSend({
                                 type: 'caption-start',
                                 id  : VCS.state.myId
                             });
            }
            VCS.ui?.showToast?.(t('autoTranslateOn'), 'success', 1800);
        }

        this.updateFabUI();
    }

    // ── Caption-UI ───────────────────────────────────────────

    updateFabUI() {
        const btn = this._view.btnCaptionRecord.get(0);
        if (!btn) {
            return;
        }
        const myLang = VCS.i18n.normalizeLang(VCS.state.currentLang);
        const peerLang = VCS.state.peerLang;
        const inCall = VCS.state.sessionState.phase === 'call';

        if (VCS.state.groupCallActive) {
            // Group-Call: sessionState.phase ist nicht 'call', daher groupCallActive als Check
            const gcActive = true;

            // Record-Button sichtbar wenn Captions oder CaptionOnly aktiv
            const visible = gcActive && (VCS.state.captionOnlyMode || VCS.state.captionsEnabled);
            btn.classList.toggle('hidden', !visible);
            btn.classList.toggle('recording', VCS.state.captionRecording);
            btn.classList.toggle('paused', VCS.state.captionPaused);
            btn.classList.toggle('disabled', false);
            btn.innerHTML = VCS.state.captionPaused ? (VCS.ui?.ICONS?.ban ?? '') : '';

            // CC-Toggle: im Group-Call immer verfuegbar (Transkription fuer Hoergeschaedigte)
            const ccBtn = $('#btnCaptionOnly')[0];
            if (ccBtn) {
                ccBtn.classList.toggle('call-cc-disabled', false);
                ccBtn.classList.toggle('hidden', false);
            }
            return;
        }

        // 1:1-Call: bestehende Logik
        const langOk = VCS.i18n.isTranslationSupported(myLang) &&
            VCS.i18n.isTranslationSupported(peerLang);
        const visible = (VCS.state.captionsEnabled && inCall && langOk)
            || (VCS.state.captionOnlyMode && inCall);
        const translationNeeded = !!peerLang && peerLang !== myLang;

        btn.classList.toggle('hidden', !visible);
        btn.classList.toggle('recording', VCS.state.captionRecording);
        btn.classList.toggle('paused', VCS.state.captionPaused);
        btn.classList.toggle('disabled', !translationNeeded && !VCS.state.captionOnlyMode);
        btn.innerHTML = VCS.state.captionPaused ? (VCS.ui?.ICONS?.ban ?? '') : '';

        const ccBtn = $('#btnCaptionOnly')[0];
        if (ccBtn) {
            const sameLang = !peerLang || peerLang === myLang;
            ccBtn.classList.toggle('call-cc-disabled', !sameLang || !inCall);
        }

        // Audio-Fader piggy-backs on the same FAB-UI re-render trigger:
        // any state change that affects caption visibility also affects
        // fader eligibility (peerLang flips, captionsEnabled toggles, ...).
        VCS.audioFader?.updateVisibility();
    }

    clearTranslated() {
        // Group-Mode: alle Tile-Slots aufraeumen (parallel zur 1:1-Bahn).
        if (VCS.state.groupCallActive) {
            this._clearGroupSpeakerState();
            const grid = document.getElementById('groupVideoGrid');
            if (grid) {
                grid.querySelectorAll('.group-video-caption').forEach(slot => {
                    slot.textContent = '';
                    slot.classList.add('hidden');
                    slot.classList.remove('caption-interim');
                });
            }
        }
        const box = this._view.translatedCaption.get(0);
        if (!box) {
            return;
        }
        const list = this._view.translatedCaptionList.get(0) || box;
        this._clearCaptionList(list, box);
    }

    clearSelfTranscript() {
        this._paceReset();
        const box = this._view.selfTranscript.get(0);
        if (!box) {
            return;
        }
        const list = this._view.selfTranscriptList.get(0) || box;
        this._clearCaptionList(list, box);
    }

    showTranslated(text, senderName, partial, phase, senderId) {
        // phaseTag normalisieren (gleiche Logik fuer 1:1 und Group)
        const phaseTag = phase === 'interim' ? 'interim' : 'final';
        const dimItalic = phaseTag === 'interim';

        // Group-Call: pro Sprecher ein eigenes Caption-Slot AM TILE.
        // Routing-Key ist die Sprecher-ID (msg.from). Server schickt fuer jeden
        // aktiven Sprecher eine eigene Pipeline -> hier landen Zeilen unterschiedlicher
        // senderId asynchron. Pro Sprecher rAF-Coalesce + eigener Hide-Timer.
        if (VCS.state.groupCallActive && senderId) {
            this._renderToGroupTile(senderId, text, !!partial, phaseTag, dimItalic);
            return;
        }

        // 1:1-Pfad (unveraendert)
        const box = this._view.translatedCaption.get(0);
        if (!box) {
            return;
        }
        const list = this._view.translatedCaptionList.get(0) || box;
        // 1:1-Pfad: bisheriger Display-Text. (Group-Mode wurde oben behandelt.)
        const displayText = text;
        // phaseTag/dimItalic sind oben bereits gesetzt — gleiche Semantik wie 1:1.
        // Visuell: "ausgegraut + kursiv" nur fuer Interim. Final-Partials rendern solide.

        if (partial) {
            // Streaming-Partials kommen 5-15x/s pro Satz waehrend OpenAI streamt.
            // Schutz fuer iPhone Safari: ScriptProcessor laeuft im Main-Thread,
            // viele DOM-Updates pro Sekunde klemmen den Audio-Thread und fuehren
            // zu Audio-Drops nach Deepgram. Wir coalescen via rAF: nur 1 DOM-Update
            // pro Frame (~16 ms = 60 Hz), latest-wins.
            this._partialPending = { displayText, list, box, dimItalic, phaseTag };
            if (this._partialRafId) return;
            this._partialRafId = requestAnimationFrame(() => {
                this._partialRafId = 0;
                const p = this._partialPending;
                this._partialPending = null;
                if (!p) return;
                this._flushPartial(p.displayText, p.list, p.box, p.dimItalic, p.phaseTag);
            });
            return;
        }

        // Stream-/Stand-Done: rAF-Pending verwerfen, weil der neue Stand alles ersetzt.
        if (this._partialRafId) {
            cancelAnimationFrame(this._partialRafId);
            this._partialRafId = 0;
            this._partialPending = null;
        }

        // Wenn die letzte Zeile ein laufender Vorlaeufiger ist (Partial oder Interim),
        // wird sie an Ort und Stelle aktualisiert. Der dim/italic-Stil richtet sich
        // nach der NEUEN phase:
        //  - phase=final  -> solide ("setzt fest")
        //  - phase=interim -> bleibt ausgegraut/kursiv (Stream-Done eines Interims,
        //    wartet auf Final)
        const lastLine = list.lastElementChild;
        if (lastLine && (lastLine._isPartial || lastLine._isInterim)) {
            lastLine._isPartial = false;
            lastLine._isInterim = phaseTag === 'interim';
            lastLine.classList.toggle('caption-interim', dimItalic);
            lastLine.textContent = displayText;
            if (lastLine._hideTimer) clearTimeout(lastLine._hideTimer);
            lastLine._hideTimer = setTimeout(() => {
                lastLine.remove();
                if (list.children.length === 0) box.classList.add('hidden');
            }, this._hideDelayMs(phaseTag === 'interim', displayText));
            box.classList.remove('hidden');
            return;
        }

        const cls = dimItalic
            ? 'translated-caption-line caption-interim'
            : 'translated-caption-line';
        this._appendCaptionLine(
            displayText, list, box, cls, VCS.Captions.CAPTION_MAX_LINES,
            phaseTag === 'interim'
        );
        const appended = list.lastElementChild;
        if (appended) appended._isInterim = dimItalic;
    }

    // Partial-Flush via rAF — siehe showTranslated() oben.
    _flushPartial(displayText, list, box, dimItalic, phaseTag) {
        const lastLine = list.lastElementChild;
        // Eine bestehende Vorlaeufer-Zeile (Partial oder Interim) wird in-place
        // geupdated. dim/italic kann zwischen Frames wechseln (z.B. Interim-Stream
        // -> Final-Stream auf derselben Zeile), darum jedes Mal nachziehen.
        if (lastLine && (lastLine._isPartial || lastLine._isInterim)) {
            lastLine.textContent = displayText;
            lastLine.classList.toggle('caption-interim', !!dimItalic);
            lastLine._isPartial = phaseTag === 'final';
            lastLine._isInterim = phaseTag === 'interim';
            if (lastLine._hideTimer) clearTimeout(lastLine._hideTimer);
            lastLine._hideTimer = setTimeout(() => {
                lastLine.remove();
                if (list.children.length === 0) box.classList.add('hidden');
            }, this._hideDelayMs(true, displayText));
            return;
        }
        const cls = dimItalic
            ? 'translated-caption-line caption-interim'
            : 'translated-caption-line';
        const before = list.lastElementChild;
        this._appendCaptionLine(
            displayText, list, box, cls, VCS.Captions.CAPTION_MAX_LINES, true
        );
        const newLine = list.lastElementChild;
        // Nur eine WIRKLICH neue Zeile markieren -- _appendCaptionLine legt
        // keine an, wenn der Text mit der letzten uebereinstimmt. Ohne den
        // Vergleich wuerde eine abgeschlossene Zeile zur Vorlaeufer-Zeile
        // umgestempelt und beim Ausblenden den neuen Satz mitnehmen.
        if (newLine && newLine !== before) {
            newLine._isPartial = phaseTag === 'final';
            newLine._isInterim = phaseTag === 'interim';
        }
    }

    // ── Group-Mode: Caption am Sprecher-Tile ────────────────
    //
    // Server schickt im Group-Call pro Sprecher eigene caption-translated-Events
    // (mit msg.from = senderId). Pro senderId wird AM TILE [data-peer-id="<id>"]
    // ein eigener .group-video-caption-Slot beschrieben. Streaming-Partials werden
    // pro Sprecher mit rAF-Coalesce gekoppelt — sonst klemmen viele DOM-Updates
    // bei mehreren parallelen Pipelines.
    //
    // Hide-Timer: Nach CAPTION_HIDE_MS wird der Slot-Inhalt geleert. Die Logik ist
    // an _appendCaptionLine angelehnt, aber pro Tile / pro Sprecher.
    _renderToGroupTile(senderId, text, partial, phaseTag, dimItalic) {
        const grid = document.getElementById('groupVideoGrid');
        if (!grid) return;
        // Sender selbst -> Self-Tile, sonst Peer-Tile per data-peer-id
        const isSelf = senderId === VCS.state.myId;
        const tileSel = isSelf
            ? '.group-video-tile--self'
            : `.group-video-tile[data-peer-id="${senderId}"]`;
        const tile = grid.querySelector(tileSel);
        if (!tile) {
            console.debug('[caption-grp] tile not found for senderId=', senderId);
            return;
        }
        const slot = tile.querySelector('.group-video-caption');
        if (!slot) return;

        const next = (text || '').trim();
        if (!next) return;

        let st = this._groupSpeakerState.get(senderId);
        if (!st) {
            st = { rafId: 0, pending: null, hideTimer: 0 };
            this._groupSpeakerState.set(senderId, st);
        }

        // Streaming-Partial: rAF-Coalesce, latest-wins.
        if (partial) {
            st.pending = { next, dimItalic, phaseTag, slot };
            if (st.rafId) return;
            st.rafId = requestAnimationFrame(() => {
                st.rafId = 0;
                const p = st.pending;
                st.pending = null;
                if (!p) return;
                this._flushGroupPartial(senderId, p.next, p.slot, p.dimItalic, p.phaseTag);
            });
            return;
        }

        // Stand-/Stream-Done: pending verwerfen, neuer Stand ersetzt alles.
        if (st.rafId) { cancelAnimationFrame(st.rafId); st.rafId = 0; }
        st.pending = null;

        // Slot rendern
        slot.textContent = next;
        slot.classList.toggle('caption-interim', !!dimItalic);
        slot.classList.remove('hidden');

        // Hide-Timer pro Tile resetten.
        if (st.hideTimer) clearTimeout(st.hideTimer);
        st.hideTimer = setTimeout(() => {
            slot.textContent = '';
            slot.classList.add('hidden');
            slot.classList.remove('caption-interim');
            st.hideTimer = 0;
        }, this._hideDelayMs(phaseTag === 'interim', next));

        console.debug(`[caption-grp] render senderId=${senderId} phase=${phaseTag} len=${next.length}`);
    }

    _flushGroupPartial(senderId, text, slot, dimItalic, phaseTag) {
        if (!slot) return;
        const next = (text || '').trim();
        if (!next) return;
        slot.textContent = next;
        slot.classList.toggle('caption-interim', !!dimItalic);
        slot.classList.remove('hidden');

        const st = this._groupSpeakerState.get(senderId);
        if (!st) return;
        if (st.hideTimer) clearTimeout(st.hideTimer);
        st.hideTimer = setTimeout(() => {
            slot.textContent = '';
            slot.classList.add('hidden');
            slot.classList.remove('caption-interim');
            st.hideTimer = 0;
        }, this._hideDelayMs(true, next));
    }

    // Wird beim Verlassen eines einzelnen Peers aufgerufen — gibt rAF/hideTimer
    // frei, damit kein Memory-Leak und keine Late-Writes ins ehemalige Tile.
    _dropGroupSpeaker(senderId) {
        const st = this._groupSpeakerState.get(senderId);
        if (!st) return;
        if (st.rafId) cancelAnimationFrame(st.rafId);
        if (st.hideTimer) clearTimeout(st.hideTimer);
        this._groupSpeakerState.delete(senderId);
    }

    // Beim Ende des Group-Calls und bei releasePipeline aufrufen.
    _clearGroupSpeakerState() {
        for (const [, st] of this._groupSpeakerState) {
            if (st.rafId) cancelAnimationFrame(st.rafId);
            if (st.hideTimer) clearTimeout(st.hideTimer);
        }
        this._groupSpeakerState.clear();
    }

    showSelfTranscript(text, partial) {
        // Vor dem Rendern: die Satzuhr haengt am partial-Flag, nicht am Text,
        // und darf nicht an den Ausstiegen weiter unten haengenbleiben.
        this._paceOnSelfText(partial);

        const box = this._view.selfTranscript.get(0);
        if (!box) {
            return;
        }
        const list = this._view.selfTranscriptList.get(0) || box;

        // Partial-Logik (analog _flushPartial fuer caption-translated):
        // Bei partial=true wird die laufende Zeile in-place upgedated, sonst
        // entsteht pro Token-Delta eine neue Zeile (Fehler in der alten Logik
        // bei Streaming-Engines wie gpt-realtime-translate).
        // partial undefined/false (Hybrid-Pfad) -> existierendes Verhalten.
        if (partial) {
            const lastLine = list.lastElementChild;
            if (lastLine && lastLine._isPartial) {
                lastLine.textContent = text;
                if (lastLine._hideTimer) clearTimeout(lastLine._hideTimer);
                lastLine._hideTimer = setTimeout(() => {
                    lastLine.remove();
                    if (list.children.length === 0) box.classList.add('hidden');
                }, this._hideDelayMs(true, text));
                box.classList.remove('hidden');
                return;
            }
            const before = list.lastElementChild;
            this._appendCaptionLine(
                text, list, box,
                'self-transcript-line',
                VCS.Captions.SELF_TRANSCRIPT_MAX_LINES,
                true
            );
            const newLine = list.lastElementChild;
            // Nur eine WIRKLICH neue Zeile als laufend markieren.
            // _appendCaptionLine legt keine an, wenn der Text mit der letzten
            // uebereinstimmt -- ohne diesen Vergleich wuerde dann eine bereits
            // abgeschlossene Zeile zur laufenden umgestempelt. Jeder weitere
            // Zwischenstand schriebe in die alte Zeile, und mit deren
            // Ausblend-Uhr verschwaende auch der neue Satz.
            if (newLine && newLine !== before) newLine._isPartial = true;
            return;
        }

        // Final: bestehende partial-Zeile festschreiben oder neue anhaengen.
        const lastLine = list.lastElementChild;
        if (lastLine && lastLine._isPartial) {
            lastLine._isPartial = false;
            lastLine.textContent = text;
            if (lastLine._hideTimer) clearTimeout(lastLine._hideTimer);
            lastLine._hideTimer = setTimeout(() => {
                lastLine.remove();
                if (list.children.length === 0) box.classList.add('hidden');
            }, this._hideDelayMs(false, text));
            box.classList.remove('hidden');
            return;
        }
        this._appendCaptionLine(
            text, list, box,
            'self-transcript-line',
            VCS.Captions.SELF_TRANSCRIPT_MAX_LINES,
            false
        );
    }

    // ── Sprechtempo-Hinweis ──────────────────────────────────

    // Getaktet vom eigenen Transkript: partial=true heisst laufender Satz,
    // partial=false heisst fertig. Eine eigene Nachricht vom Server braucht es
    // dafuer nicht -- der Azure-Pfad setzt das Flag bereits (caption.js
    // AZURE_HOOKS.sendTranscript).
    _paceOnSelfText(partial) {
        if (!partial) {
            this._paceEndSentence();
            return;
        }

        // Leerlauf-Waechter: bleibt das Final aus (Verbindung weg, Pipeline
        // gestoppt), soll das Symbol nicht ewig stehen.
        clearTimeout(this._paceIdleTimer);
        this._paceIdleTimer = setTimeout(
            () => this._paceEndSentence(), VCS.Captions.SPEECH_PACE_IDLE_MS
        );

        // Laeuft die Uhr fuer diesen Satz schon, nicht neu starten -- sonst
        // schiebt jeder Zwischenstand die Schwelle vor sich her und sie wird
        // bei schnellem Sprechen nie erreicht.
        if (this._paceShowTimer || this._paceVisible) {
            return;
        }
        this._paceShowTimer = setTimeout(() => {
            this._paceShowTimer = 0;
            this._paceShow();
        }, VCS.Captions.SPEECH_PACE_THRESHOLD_MS);
    }

    _paceEndSentence() {
        clearTimeout(this._paceShowTimer);
        this._paceShowTimer = 0;
        clearTimeout(this._paceIdleTimer);
        this._paceIdleTimer = 0;
        this._paceHide();
    }

    _paceShow() {
        const el = this._view.speechPaceHint;
        if (!el.length) {
            return;
        }
        this._paceVisible = true;
        el.removeClass('hidden');

        // Beim allerersten Mal die Erklaerung dazu, danach nie wieder von
        // selbst. Wer sie spaeter noch einmal braucht, tippt das Symbol an.
        let seen = false;
        try {
            seen = localStorage.getItem(VCS.Captions.SPEECH_PACE_SEEN_KEY) === '1';
        } catch (_) {
        }
        if (!seen) {
            this._paceShowExplain(true);
        }
    }

    _paceHide() {
        this._paceVisible = false;
        // Laeuft gerade die Erklaerung, bleibt das Element stehen, bis sie
        // durch ist. Sonst reisst ausgerechnet die Sprechpause -- also der
        // Erfolgsfall -- den Text mitten im Lesen weg.
        if (this._paceExplainTimer) {
            return;
        }
        this._view.speechPaceHint.addClass('hidden');
    }

    // auto=true: automatische Erstanzeige, die den Merker setzen darf.
    // Ohne Argument: der Nutzer hat das Symbol angetippt.
    _paceShowExplain(auto) {
        const el = this._view.speechPaceHint;
        const txt = this._view.speechPaceHintText;
        if (!el.length || !txt.length) {
            return;
        }
        el.removeClass('hidden');
        txt.removeClass('hidden');

        clearTimeout(this._paceExplainTimer);
        this._paceExplainTimer = setTimeout(() => {
            this._paceExplainTimer = 0;
            txt.addClass('hidden');
            // Erst jetzt gilt sie als gelesen. Beim Einblenden gesetzt waere
            // sie schon verbraucht, wenn der Call in derselben Sekunde endet.
            if (auto) {
                try {
                    localStorage.setItem(VCS.Captions.SPEECH_PACE_SEEN_KEY, '1');
                } catch (_) {
                }
            }
            // Das Symbol kann laengst wieder aus sein -- dann jetzt mit weg.
            if (!this._paceVisible) {
                el.addClass('hidden');
            }
        }, VCS.Captions.SPEECH_PACE_EXPLAIN_MS);
    }

    _paceReset() {
        clearTimeout(this._paceShowTimer);
        this._paceShowTimer = 0;
        clearTimeout(this._paceIdleTimer);
        this._paceIdleTimer = 0;
        // Bewusst ohne den Merker zu setzen: eine Erklaerung, die das
        // Call-Ende abgeschnitten hat, wurde nicht gelesen.
        clearTimeout(this._paceExplainTimer);
        this._paceExplainTimer = 0;
        this._paceVisible = false;
        this._view.speechPaceHint.addClass('hidden');
        this._view.speechPaceHintText.addClass('hidden');
    }

    // ── Pro-Trial ────────────────────────────────────────────

    updateProTrialUI() {
        const s = VCS.state;
        // Backend only sends a positive proTrialSecondsTotal to trial users
        // (see main-server.js chat-authed payload). Robust against plan renames.
        if (!s.chatProTrialSecondsTotal) return;
        const remaining = s.chatProTrialSecondsTotal - s.chatProTrialSecondsUsed;
        if (remaining <= 0) return;
        const min = Math.floor(remaining / 60);
        const sec = remaining % 60;
        const text = (t('proTrialRemaining') || 'Pro-Test: {min}:{sec} verbleibend')
            .replace('{min}', min).replace('{sec}', String(sec).padStart(2, '0'));
        VCS.ui?.showToast?.(text, 'info', 3000);
    }

    onProTrialExpired() {
        this.releasePipeline();
    }

    // ── Init ─────────────────────────────────────────────────

    init() {
        this._view.btnCaptionRecord.on('click', () => {
            this.togglePause();
        });
    }

    // ── Private Methoden ─────────────────────────────────────

    // WebSocket-Nachricht senden
    _wsSend(obj) {
        VCS.signaling?.send(obj);
    }

    // Float32 → Int16 Mono mit optionalem Downsampling
    _convertFloat32ToInt16Mono(input, inputSampleRate, targetSampleRate) {
        if (!input || input.length === 0) {
            return new Int16Array(0);
        }

        const clamp = v => v < 0 ? v * 0x8000 : v * 0x7FFF;

        // Direkte Konvertierung wenn kein Downsampling nötig
        if (!Number.isFinite(inputSampleRate) || inputSampleRate <= 0 ||
            inputSampleRate <= targetSampleRate) {
            const out = new Int16Array(input.length);
            for (let i = 0; i < input.length; i++) {
                out[i] = clamp(Math.max(-1, Math.min(1, input[i])));
            }
            return out;
        }

        // Downsampling per Mittelwert pro Ziel-Sample
        const ratio = inputSampleRate / targetSampleRate;
        const outputLength = Math.max(1, Math.floor(input.length / ratio));
        const output = new Int16Array(outputLength);
        let inputOffset = 0;

        for (let outIdx = 0; outIdx < outputLength; outIdx++) {
            const nextOffset = Math.round((outIdx + 1) * ratio);
            let accum = 0;
            let count = 0;
            for (let i = inputOffset; i < nextOffset && i < input.length; i++) {
                accum += input[i];
                count++;
            }
            const sample = count > 0 ? (accum / count) : 0;
            output[outIdx] = clamp(Math.max(-1, Math.min(1, sample)));
            inputOffset = nextOffset;
        }

        return output;
    }

    // Alle Zeilen in einer Caption-Liste entfernen und Box verstecken
    _clearCaptionList(list, box) {
        Array.from(list.children).forEach(line => {
            if (line._hideTimer) {
                clearTimeout(line._hideTimer);
                line._hideTimer = null;
            }
        });
        list.innerHTML = '';
        if (list === box) {
            box.textContent = '';
        }
        box.classList.add('hidden');
    }

    // Wie lange eine Caption-Zeile stehen bleibt.
    //
    // NUR fuer den Azure-Pfad. Jede andere Engine bekommt weiterhin die feste
    // Uhr -- deren Anzeige funktioniert, und ein Umbau daran waere ein Risiko
    // ohne Anlass.
    //
    // Das Azure-Problem: running=true heisst, der Satz laeuft noch
    // (interim/partial). Dann darf die Lesezeit NICHT laufen. Vorher setzte
    // jeder Zwischenstand die 8 s neu, und weil Azure sechs Zwischenstaende je
    // Satz schickt, stand der graue, staendig umgebaute Text beliebig lange --
    // der fertige weisse Satz bekam seine 8 s dagegen genau einmal, weil danach
    // keine Aktualisierung mehr kam. Lesbar war damit ausgerechnet der kuerzere
    // Teil. Ein laufender Satz haelt jetzt nur noch das Not-Netz, die echte
    // Lesezeit beginnt mit dem Final und richtet sich nach der Textlaenge.
    _hideDelayMs(running, text) {
        if (VCS.state.captionEngine !== 'azure-speech') {
            return VCS.Captions.CAPTION_HIDE_MS;
        }
        if (running) {
            return VCS.Captions.CAPTION_STALE_MS;
        }
        const chars = (text || '').trim().length;
        return Math.min(
            VCS.Captions.CAPTION_HIDE_MAX_MS,
            Math.max(
                VCS.Captions.CAPTION_HIDE_MS,
                chars * VCS.Captions.CAPTION_MS_PER_CHAR
            )
        );
    }

    // Caption-Zeile hinzufügen (mit Deduplizierung + Auto-Hide)
    _appendCaptionLine(text, list, box, cssClass, maxLines, running) {
        const next = (text || '').trim();
        if (!next) {
            return;
        }

        const HIDE_MS = this._hideDelayMs(running, next);

        // Letzte Zeile aktualisieren wenn identischer Text
        const lastLine = list.lastElementChild;
        if (lastLine && lastLine.textContent === next) {
            if (lastLine._hideTimer) {
                clearTimeout(lastLine._hideTimer);
            }
            lastLine._hideTimer = setTimeout(() => {
                lastLine.remove();
                if (list.children.length === 0) {
                    box.classList.add('hidden');
                }
            }, HIDE_MS);
            box.classList.remove('hidden');
            return;
        }

        // Neue Zeile anhängen
        const line = document.createElement('div');
        line.className = cssClass;
        line.textContent = next;
        list.appendChild(line);

        // Älteste Zeilen entfernen wenn Limit überschritten
        while (list.children.length > maxLines) {
            const oldest = list.firstElementChild;
            if (!oldest) {
                break;
            }
            if (oldest._hideTimer) {
                clearTimeout(oldest._hideTimer);
                oldest._hideTimer = null;
            }
            oldest.remove();
        }

        box.classList.remove('hidden');

        line._hideTimer = setTimeout(() => {
            line.remove();
            if (list.children.length === 0) {
                box.classList.add('hidden');
            }
        }, HIDE_MS);
    }
};
